import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import * as yup from 'yup'
import { useIntl } from 'react-intl'
import { useFormik } from 'formik'
import { useNavigate, useParams } from 'react-router-dom'
import styled from '@emotion/styled'
import { Box, Button, CircularProgress, LinearProgress } from '@mui/material'
import { courseApiSlice } from '../../../../features/course/courseSlice'
import { requiredString, requiredStringMinThree } from '../../../../utils/validation'
import { CourseState } from '../../../../features/types'
import { H3 } from '../../../../layouts/TextStyles'
import AddCourseContent from './components/AddCourseContent'
import { ui } from '../../../../utils/uiTokens'

const CoursePage = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const { formatMessage } = useIntl()
    const [imageKz, setImageKz] = useState(null)
    const [imageRu, setImageRu] = useState(null)

    const editMode = !!id

    const { data: editCourse = null, isFetching } =
        courseApiSlice.useGetCourseByIdQuery(+id, {
            skip: !editMode,
            refetchOnMountOrArgChange: true,
        })

    const [createCourse, { isLoading }] =
        courseApiSlice.useCreateCourseMutation()
    const [updateCourse, { isLoading: updateLoading }] =
        courseApiSlice.useUpdateCourseMutation()

    const course: CourseState = {
        id: null,
        titleKz: '',
        titleRu: '',
        descriptionKz: '',
        descriptionRu: '',
        imageKz: '',
        imageRu: '',
        courseType: '',
        videoCourseType: '',
        sequentialLearningEnabled: false,
        strictFromMonth: null,
        strictFromWeek: null,
    }

    const formData = new FormData()

    const validationSchema = yup.object().shape({
        titleKz: requiredString({ formatMessage }),
        titleRu: requiredString({ formatMessage }),
        descriptionKz: requiredString({ formatMessage }),
        descriptionRu: requiredString({ formatMessage }),
        imageKz: !editMode ? yup.mixed().required('Сурет жүктеңіз') : null,
        imageRu: !editMode ? yup.mixed().required('Загрузите фото') : null,
        courseType: requiredString({ formatMessage }),
        videoCourseType: requiredString({ formatMessage }),
    })

    const formik = useFormik({
        initialValues: course,
        validationSchema: validationSchema,
        validateOnChange: false,
        validateOnBlur: false,

        onSubmit: async () => {
            formData.append('titleKz', formik.values.titleKz)
            formData.append('titleRu', formik.values.titleRu)
            formData.append('descriptionKz', formik.values.descriptionKz)
            formData.append('descriptionRu', formik.values.descriptionRu)
            formData.append('imageKz', formik.values.imageKz)
            formData.append('imageRu', formik.values.imageRu)
            formData.append("courseType", formik.values.courseType)
            formData.append("videoCourseType", formik.values.videoCourseType)
            formData.append('sequentialLearningEnabled', String(!!formik.values.sequentialLearningEnabled))
            // пустая отсечка = строгость с начала курса
            formData.append('strictFromMonth', String(formik.values.strictFromMonth || 0))
            formData.append('strictFromWeek', String(formik.values.strictFromMonth ? formik.values.strictFromWeek || 1 : 0))

            if (editMode) {
                await handleUpdateCourse()
            } else {
                await handleCreateCourse()
            }
        },
    })

    const handleCreateCourse = async () => {
        try {
            await createCourse(formData).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
            navigate('/courses')
            window.location.reload()
        } catch (error) {}
    }

    const handleUpdateCourse = async () => {
        formData.append('id', `${editCourse?.id}`)

        try {
            await updateCourse(formData).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
            navigate('/courses')
            window.location.reload()
        } catch (error) {}
    }

    const onImageKzChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        formik.setFieldValue('imageKz', e.target.files[0])
        setImageKz(URL.createObjectURL(e.target.files[0]))
    }

    const onImageRuChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        formik.setFieldValue('imageRu', e.target.files[0])
        setImageRu(URL.createObjectURL(e.target.files[0]))
    }

    useEffect(() => {
        if (editCourse) {
            formik.setValues(editCourse)
            setImageKz(editCourse.imageKz)
            setImageRu(editCourse.imageRu)
        }
    }, [editCourse])

    return (
        <Wrapper>
            <Content>
                <Header>
                    <Title>
                        <H3>{formatMessage({ id: 'addCourse' })}</H3>
                    </Title>
                </Header>

                {isFetching ? (
                    <LinearProgress />
                ) : (
                    <AddCourseContent
                        editCourse={editCourse}
                        onImageKzChange={onImageKzChange}
                        onImageRuChange={onImageRuChange}
                        formik={formik}
                        imageKz={imageKz}
                        imageRu={imageRu}
                    />
                )}

                <Box sx={{ display: 'flex', justifyContent: 'end', m: 2 }}>
                    <Button
                        variant={'outlined'}
                        sx={{ mr: 1 }}
                        onClick={() => navigate('/courses')}
                        disabled={isLoading || updateLoading}
                    >
                        {formatMessage({ id: 'cancel' })}
                    </Button>

                    <Button
                        variant={'contained'}
                        sx={{ width: '120px' }}
                        onClick={() => formik.handleSubmit()}
                        disabled={isLoading || updateLoading}
                    >
                        {isLoading || updateLoading ? (
                            <CircularProgress
                                size={20}
                                style={{ marginRight: '8px' }}
                            />
                        ) : (
                            formatMessage({ id: 'save' })
                        )}
                    </Button>
                </Box>
            </Content>
        </Wrapper>
    )
}

export default CoursePage

const Wrapper = styled.div`
    margin: 10px;
    border-radius: 16px;
`

const Content = styled.div`
    background-color: ${ui.card};
    border-radius: 16px;
    padding: 20px;
    min-height: 80vh;
`
const Title = styled.div`
    margin-bottom: 20px;
    text-align: left;
    display: flex;
    align-items: center;
`

const Header = styled.header`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`
